/* eslint-disable @typescript-eslint/no-use-before-define */
import { B64ABC, B64TAB, CRC32TAB, HEXTAB } from "../constants";
import { CreateBufferMethods } from "../types";

function encodeBase64(value: string) {
  return createBuffer().addString(value.toString()).toBase64String();
}

export function createBuffer(): CreateBufferMethods {
  let _ndx: number;
  let _buf: number[];
  const _this = {
    clear() {
      _ndx = 0;
      _buf = [];

      return this;
    },
    nBytes() {
      return _ndx;
    },
    length() {
      return _buf.length;
    },
    toString() {
      const b = [];

      for (let i = _buf.length; i-- > 0; ) {
        b[i] = _this.charCodeAt(i);
      }

      return b.join("");
    },
    toHexString() {
      const b = [];

      for (let i = _ndx; i-- > 0; ) {
        b[i] = HEXTAB[_this.byteCodeAt(i)];
      }

      return b.join("");
    },
    toArray() {
      const b = [];

      for (let i = 0; i < _ndx; ) {
        b.push(_this.byteCodeAt(i++));
      }

      return b;
    },
    toBase64String() {
      const b = [];

      b.length = ((_ndx + 2) / 3) << 2;
      for (let i = 0, j = 0, n = 0, a = 0; ; ++i) {
        if (i < _ndx) {
          a = (a << 8) | _this.byteCodeAt(i);
          j += 8;
          do {
            b[n++] = B64TAB.charAt((a >>> (j -= 6)) & 63);
          } while (j > 5);

          continue;
        }

        if (j) {
          b[n++] = B64TAB.charAt((a << (6 - j)) & 63);
        }

        while (n & 3) {
          b[n++] = "=";
        }

        return b.join("");
      }
    },
    byteCodeAt(i: number) {
      return (_buf[i >> 1] >> (8 * (i & 1))) & 255;
    },

    charCodeAt(i: number) {
      return String.fromCharCode(_buf[i]);
    },
    addArray(value: number[]) {
      if (value !== null) {
        for (let i = 0; i < value.length; ) {
          _this.addByte(value[i++]);
        }
      }

      return _this;
    },
    addByte(value: number) {
      let newValue = value;

      newValue &= 255;
      if (_ndx & 1) {
        _buf[_ndx >> 1] |= newValue << 8;
      } else {
        _buf[_ndx >> 1] = newValue;
      }

      ++_ndx;

      return _this;
    },
    addWord(value: number) {
      _this.addByte(value);
      _this.addByte(value >> 8);

      return _this;
    },
    addInt(value: number) {
      _this.addByte(value);
      _this.addByte(value >> 8);
      _this.addByte(value >> 16);
      _this.addByte(value >> 24);

      return _this;
    },
    addString(value: string) {
      if (value !== null) {
        for (let i = 0; i < value.length; ) {
          _this.addWord(value.charCodeAt(i++));
        }
      }

      return _this;
    },
    inverse() {
      if (_ndx > 1) {
        const result = createBuffer();

        for (let i = _ndx; i--; ) {
          result.addByte(_this.byteCodeAt(i));
        }

        return result;
      }

      return _this;
    },
    decodeFromHex(value: string) {
      let newValue = value;

      if (newValue !== null) {
        if (newValue.length & 1) {
          newValue = `0${newValue}`;
        }

        for (let i = 0; i < newValue.length; i += 2) {
          _this.addByte(parseInt(newValue.substring(i, i + 2), 16));
        }
      }

      return _this;
    },
    unpack() {
      const b = [];

      for (let i = 0; i < _ndx; ) {
        b[i] = String.fromCharCode(_this.byteCodeAt(i++));
      }

      return b.join("");
    },
    decodeFromBase64(value: string) {
      if (value !== null) {
        let a = 0;
        let i = 0;
        let j = 0;
        let v = 0;
        let cb = value.length;

        do {
          if (cb > 0) {
            v = B64ABC[value.charCodeAt(i++) & 255];
            switch (v) {
              case 65:
                cb = 0;
                break;

              case 64:
                continue;

              default:
                a = (a << 6) | v;
                j += 6;
                if (j < 8) {
                  continue;
                }
            }
          }

          while (j > 7) {
            _this.addByte((a >>> (j -= 8)) & 255);
          }
        } while (cb-- > 0);
      }

      return _this;
    },
    pack(value: string | null) {
      if (value !== null) {
        const newValue = value.toString();

        for (let i = 0; i < newValue.length; ) {
          _this.addByte(newValue.charCodeAt(i++));
        }
      }

      return _this;
    },
    encodeToBase64(value: string) {
      _this.addString(encodeBase64(value));

      return _this;
    },
    encodeToBase64AndPack(value: string) {
      _this.pack(encodeBase64(value));

      return _this;
    },
    crc() {
      let v = 0xffffffff;

      for (let i = 0; i < _ndx; ) {
        v = CRC32TAB[(v ^ _this.byteCodeAt(i++)) & 255] ^ (v >>> 8);
      }

      return ~v;
    },
    merge(buff: CreateBufferMethods) {
      if (buff !== null) {
        if (buff.nBytes && buff.byteCodeAt) {
          for (let i = 0; i < buff.nBytes(); ) {
            _this.addByte(buff.byteCodeAt(i++));
          }
        } else {
          return _this.addString(buff.toString());
        }
      }

      return _this;
    },
  };

  _this.clear();

  return _this;
}
